import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';

type ChainItem = { lat: number; lng: number; label: string };

type Projected = {
  sx: number;
  sy: number;
  x: number;
  y: number;
  z: number;
  visible: boolean;
  alpha: number;
};

const CHAIN: ChainItem[] = [
  { lat: 44, lng: -20, label: '🇵🇹 Portugal' },
  { lat: 40, lng: 15, label: '🇪🇸 Spain' },
  { lat: 48, lng: 50, label: '🇫🇷 France' },
  { lat: 46, lng: 85, label: '🇮🇪 Ireland' },
  { lat: 51, lng: 120, label: '🇬🇧 United Kingdom' },
  { lat: 44, lng: 155, label: '🇳🇱 Netherlands' },
  { lat: 42, lng: -170, label: '🇧🇪 Belgium' },
  { lat: 48, lng: -135, label: '🇩🇰 Denmark' },
  { lat: 46, lng: -100, label: '🇩🇪 Germany' },
  { lat: 53, lng: -65, label: '🇳🇴 Norway' },
  { lat: 50, lng: -30, label: '🇸🇪 Sweden' },
  { lat: 48, lng: 5, label: '🇫🇮 Finland' },
  { lat: 44, lng: 40, label: '🇵🇱 Poland' },
  { lat: 42, lng: 75, label: '🇦🇹 Austria' },
  { lat: 40, lng: 110, label: '🇮🇹 Italy' },
];

const STEP_MS = 2800;

const STATS = [
  { value: '14+', label: 'Active Markets' },
  { value: '15+', label: 'Carrier Partners' },
  { value: '99.9%', label: 'Platform Uptime' },
];

function latLngToXYZ(lat: number, lng: number, r: number): [number, number, number] {
  const phi = ((90 - lat) * Math.PI) / 180;
  const theta = ((lng + 180) * Math.PI) / 180;
  return [
    -(r * Math.sin(phi) * Math.cos(theta)),
    -(r * Math.cos(phi)),
    r * Math.sin(phi) * Math.sin(theta),
  ];
}

function applyRotX(x: number, y: number, z: number, a: number): [number, number, number] {
  return [x, y * Math.cos(a) - z * Math.sin(a), y * Math.sin(a) + z * Math.cos(a)];
}

function applyRotY(x: number, y: number, z: number, a: number): [number, number, number] {
  return [x * Math.cos(a) + z * Math.sin(a), y, -x * Math.sin(a) + z * Math.cos(a)];
}

function project(x: number, y: number, z: number, cx: number, cy: number, fov: number): [number, number] {
  const s = fov / (fov + z);
  return [x * s + cx, y * s + cy];
}

@Component({
  selector: 'app-home-coverage-globe',
  standalone: false,
  templateUrl: './home-coverage-globe.component.html',
  styleUrl: './home-coverage-globe.component.scss',
})
export class HomeCoverageGlobeComponent implements AfterViewInit, OnDestroy {
  @ViewChild('globeCanvas', { static: true })
  globeCanvas!: ElementRef<HTMLCanvasElement>;

  stats = STATS;

  private dots: Array<[number, number, number]> = [];
  private rafId: number | null = null;
  private stepIntervalId: ReturnType<typeof setInterval> | null = null;
  private progressStart = 0;
  private lastCanvasW = 0;
  private lastCanvasH = 0;
  private observer: IntersectionObserver | null = null;
  private isVisible = false;

  private rotY = 0.18;
  private rotX = 0.18;

  private drag = {
    active: false,
    startX: 0,
    startY: 0,
    startRotY: 0,
    startRotX: 0,
  };

  private windowStart = 0;
  private stepProgress = 0;
  private time = 0;
  private drawErrored = false;

  ngAfterViewInit(): void {
    this.buildDotField();
    this.progressStart = performance.now();

    this.stepIntervalId = setInterval(() => {
      this.windowStart = (this.windowStart + 1) % CHAIN.length;
    }, STEP_MS);

    const canvas = this.globeCanvas?.nativeElement;
    if (canvas && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        entries => {
          const visible = entries[0]?.isIntersecting ?? false;
          this.isVisible = visible;
          if (visible && this.rafId == null) {
            this.progressStart = performance.now() - (this.time * 1000);
            this.rafId = requestAnimationFrame(this.draw);
          }
        },
        { threshold: 0.05 }
      );
      this.observer.observe(canvas);
    } else {
      this.isVisible = true;
      this.rafId = requestAnimationFrame(this.draw);
    }
  }

  ngOnDestroy(): void {
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.stepIntervalId) {
      clearInterval(this.stepIntervalId);
      this.stepIntervalId = null;
    }
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  onPointerDown(event: PointerEvent): void {
    this.drag.active = true;
    this.drag.startX = event.clientX;
    this.drag.startY = event.clientY;
    this.drag.startRotY = this.rotY;
    this.drag.startRotX = this.rotX;

    const canvas = this.globeCanvas?.nativeElement;
    canvas?.setPointerCapture?.(event.pointerId);
  }

  onPointerMove(event: PointerEvent): void {
    if (!this.drag.active) return;
    this.rotY = this.drag.startRotY + (event.clientX - this.drag.startX) * 0.005;
    this.rotX = Math.max(
      -0.8,
      Math.min(0.8, this.drag.startRotX + (event.clientY - this.drag.startY) * 0.005)
    );
  }

  onPointerUp(): void {
    this.drag.active = false;
  }

  private buildDotField(): void {
    const dots: Array<[number, number, number]> = [];
    const gr = (1 + Math.sqrt(5)) / 2;
    for (let i = 0; i < 1200; i++) {
      const theta = (2 * Math.PI * i) / gr;
      const phi = Math.acos(1 - (2 * (i + 0.5)) / 1200);
      dots.push([
        Math.cos(theta) * Math.sin(phi),
        Math.cos(phi),
        Math.sin(theta) * Math.sin(phi),
      ]);
    }
    this.dots = dots;
  }

  private draw = (): void => {
    const canvas = this.globeCanvas?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.rafId = requestAnimationFrame(this.draw);
      return;
    }

    try {
      const now = performance.now();
      const elapsed = (now - this.progressStart) % STEP_MS;
      this.stepProgress = elapsed / STEP_MS;

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      if (w === 0 || h === 0) {
        return;
      }

      const targetW = Math.floor(w * dpr);
      const targetH = Math.floor(h * dpr);
      if (canvas.width !== targetW || canvas.height !== targetH) {
        canvas.width = targetW;
        canvas.height = targetH;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        this.lastCanvasW = targetW;
        this.lastCanvasH = targetH;
      }

      const cx = w / 2;
      const cy = h / 2;
      const radius = Math.min(w, h) * 0.4;
      const fov = 640;

      if (!this.drag.active) {
        this.rotY += 0.0012;
      }

      this.time += 0.016;
      const time = this.time;

      ctx.clearRect(0, 0, w, h);

      // Ambient glow
      const glow = ctx.createRadialGradient(cx, cy, radius * 0.6, cx, cy, radius * 1.6);
      glow.addColorStop(0, 'rgba(43,95,199,0.04)');
      glow.addColorStop(1, 'rgba(7,29,73,0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, w, h);

      // Outline
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(7,29,73,0.06)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Dot field
      for (const [bx, by, bz] of this.dots) {
        let x = bx * radius;
        let y = by * radius;
        let z = bz * radius;
        [x, y, z] = applyRotX(x, y, z, this.rotX);
        [x, y, z] = applyRotY(x, y, z, this.rotY);
        if (z > 0) continue;
        const [sx, sy] = project(x, y, z, cx, cy, fov);
        const depthAlpha = Math.max(0.04, 1 - (z + radius) / (2 * radius));
        ctx.beginPath();
        ctx.arc(sx, sy, 0.8 + depthAlpha * 0.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(7,29,73,${(depthAlpha * 0.22).toFixed(2)})`;
        ctx.fill();
      }

    const len = CHAIN.length;
    const ws = this.windowStart;
    const prog = this.stepProgress;

    const idxA = ws % len;
    const idxB = (ws + 1) % len;
    const idxC = (ws + 2) % len;

    const fadeOutA = prog > 0.7 ? 1 - (prog - 0.7) / 0.3 : 1;
    const fadeInC = prog < 0.3 ? prog / 0.3 : 1;

    const projectCountry = (lat: number, lng: number): Projected => {
      let [x, y, z] = latLngToXYZ(lat, lng, radius);
      [x, y, z] = applyRotX(x, y, z, this.rotX);
      [x, y, z] = applyRotY(x, y, z, this.rotY);
      const visible = z < radius * 0.35;
      const [sx, sy] = project(x, y, z, cx, cy, fov);
      return { sx, sy, x, y, z, visible, alpha: 1 };
    };

    const cA = { ...projectCountry(CHAIN[idxA].lat, CHAIN[idxA].lng), alpha: fadeOutA };
    const cB = { ...projectCountry(CHAIN[idxB].lat, CHAIN[idxB].lng), alpha: 1 };
    const cC = { ...projectCountry(CHAIN[idxC].lat, CHAIN[idxC].lng), alpha: fadeInC };

    const arcCtrl = (a: Projected, b: Projected, lift = 1.28): [number, number] => {
      const mx = (a.x + b.x) / 2;
      const my = (a.y + b.y) / 2;
      const mz = (a.z + b.z) / 2;
      const l = Math.sqrt(mx * mx + my * my + mz * mz) || 1;
      return project(
        (mx / l) * radius * lift,
        (my / l) * radius * lift,
        (mz / l) * radius * lift,
        cx,
        cy,
        fov
      );
    };

    const drawArc = (from: Projected, to: Projected, phaseOffset: number): void => {
      if (from.z >= radius * 0.45 || to.z >= radius * 0.45) return;
      const [cpx, cpy] = arcCtrl(from, to);
      const arcAlpha = Math.min(from.alpha, to.alpha);

      ctx.beginPath();
      ctx.moveTo(from.sx, from.sy);
      ctx.quadraticCurveTo(cpx, cpy, to.sx, to.sy);
      ctx.strokeStyle = `rgba(43,95,199,${(0.3 * arcAlpha).toFixed(2)})`;
      ctx.lineWidth = 1.2;
      ctx.stroke();

      const t = (time * 0.38 + phaseOffset) % 1;
      const tx = (1 - t) ** 2 * from.sx + 2 * (1 - t) * t * cpx + t ** 2 * to.sx;
      const ty = (1 - t) ** 2 * from.sy + 2 * (1 - t) * t * cpy + t ** 2 * to.sy;

      ctx.beginPath();
      ctx.arc(tx, ty, 2.6, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(43,95,199,${(0.95 * arcAlpha).toFixed(2)})`;
      ctx.fill();

      ctx.beginPath();
      ctx.arc(tx, ty, 5, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(43,95,199,${(0.12 * arcAlpha).toFixed(2)})`;
      ctx.fill();
    };

    drawArc(cA, cB, 0);
    drawArc(cB, cC, 0.5);

    const drawMarker = (c: Projected, label: string, pulseOffset: number): void => {
      if (!c.visible) return;
      const a = c.alpha;
      const pulse = Math.sin(time * 1.9 + pulseOffset) * 0.5 + 0.5;

      ctx.beginPath();
      ctx.arc(c.sx, c.sy, 5 + pulse * 6, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(43,95,199,${((0.12 + pulse * 0.1) * a).toFixed(2)})`;
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(c.sx, c.sy, 3.2, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(43,95,199,${(0.38 * a).toFixed(2)})`;
      ctx.lineWidth = 1;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(c.sx, c.sy, 3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(43,95,199,${a.toFixed(2)})`;
      ctx.fill();

      ctx.font = "10px system-ui, 'Segoe UI Emoji', 'Apple Color Emoji', 'Noto Color Emoji', sans-serif";
      const tw = ctx.measureText(label).width;
      const lx = c.sx + 10;
      const ly = c.sy + 4;
      const padX = 5;
      const pillH = 14;

      ctx.fillStyle = `rgba(255,255,255,${(0.9 * a).toFixed(2)})`;
      ctx.beginPath();
      const roundRect = (ctx as any).roundRect as
        | ((x: number, y: number, w: number, h: number, radii?: number | number[]) => void)
        | undefined;
      if (roundRect) {
        roundRect.call(ctx, lx - padX, ly - pillH + 4, tw + padX * 2 + 2, pillH, 3);
      } else {
        ctx.rect(lx - padX, ly - pillH + 4, tw + padX * 2 + 2, pillH);
      }
      ctx.fill();

      ctx.fillStyle = `rgba(43,95,199,${(0.92 * a).toFixed(2)})`;
      ctx.fillText(label, lx, ly);
    };

    drawMarker(cA, CHAIN[idxA].label, 0);
    drawMarker(cB, CHAIN[idxB].label, 2.2);
    drawMarker(cC, CHAIN[idxC].label, 4.4);
  } catch (err) {
    if (!this.drawErrored) {
      console.error('Coverage globe animation error', err);
      this.drawErrored = true;
    }
  } finally {
    if (!this.drawErrored && this.isVisible) {
      this.rafId = requestAnimationFrame(this.draw);
    } else {
      this.rafId = null;
    }
  }
};
}
